import PointerInteraction from 'ol/interaction/Pointer';
import { noModifierKeys } from 'ol/events/condition';
import Event from 'ol/events/Event';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import { containsCoordinate } from 'ol/extent';

import { extentAt, photoFeatureLocation } from './photo-layer-utils';
import { PhotoLayerFeatureProperty as FeatureProperty } from './photo-layer-feature-property';
import { DEFAULT_IMAGE_SIZE } from './photo-layer-feature-layout';

export const PhotoLayerEventType = {
  // Triggered when a photo in the exploded layout is clicked
  PHOTO_CLICK: 'photo-click'
};

class PhotoLayerEvent extends Event {

  constructor (type, feature, originalEvent) {
    super(type);
    this.feature = feature;
    this.originalEvent = originalEvent;
  }

}

// Implements photo drag and click functionality
export class PhotoLayerPointerInteraction extends PointerInteraction {

  constructor (photoLayerSource) {
    super({
      handleEvent: event => this._handleEvent(event),
      handleDownEvent: event => this._handleDownEvent(event),
      handleDragEvent: event => this._handleDragEvent(event),
      handleUpEvent: event => this._handleUpEvent(event)
    });
    this.setActive(false);
    this._source = photoLayerSource;
  }

  _handleEvent (event) {
    if (noModifierKeys(event) && event.type === MapBrowserEventType.CLICK) {
      return !this._handleClickEvent(event);
    }
    return super.handleEvent(event);
  }

  _handleClickEvent (event) {
    const { feature } = this._findPhotoAttachment(event, false);
    if (feature) {
      const photoLayerEvent = new PhotoLayerEvent(PhotoLayerEventType.PHOTO_CLICK, feature, event);
      this.dispatchEvent(photoLayerEvent);
      return photoLayerEvent.propagationStopped;
    }
    return false;
  }

  _handleDownEvent (event) {
    if (!noModifierKeys(event)) {
      return false;
    }
    this._dragInfo = undefined;

    const { feature, attachmentIndex, attachmentPixel } = this._findPhotoAttachment(event);
    if (feature) {
      this._dragInfo = {
        feature,
        attachmentIndex,
        originalAttachmentPixel: attachmentPixel,
        dragStartPixel: event.pixel
      };
    }

    return !!this._dragInfo;
  }

  _findPhotoAttachment (event, searchOnlyExploded = true) {
    const clickPoint = event.pixel;
    const features = this._source.getSortedFeatures();
    for (let i = features.length - 1; i >= 0; i--) {
      const feature = features[i];
      const animationProgress = feature.get(FeatureProperty.ANIMATION_PROGRESS);
      if (animationProgress !== undefined && animationProgress < 1) {
        continue;
      }

      const layout = feature.get(FeatureProperty.LAYOUT);
      const location = photoFeatureLocation(feature);
      const featureCenterPixel = event.map.getPixelFromCoordinate(location);

      if (layout) {
        const attachmentIndex = layout.getAttachmentAt(featureCenterPixel, clickPoint);
        if (attachmentIndex >= 0) {
          return {
            feature,
            attachmentIndex,
            attachmentPixel: layout.getAttachmentPosition(attachmentIndex).slice()
          };
        }
      } else if (!searchOnlyExploded) {
        const nonExplodedStyleEnvelope = extentAt(featureCenterPixel, DEFAULT_IMAGE_SIZE);
        if (containsCoordinate(nonExplodedStyleEnvelope, clickPoint)) {
          return {
            feature,
            attachmentIndex: 0,
            attachmentPixel: featureCenterPixel
          };
        }
      }
    }
    return {};
  }

  _handleDragEvent (event) {
    if (!this._dragInfo) {
      return false;
    }

    const { feature, attachmentIndex, originalAttachmentPixel, dragStartPixel } = this._dragInfo;

    const dx = event.pixel[0] - dragStartPixel[0];
    const dy = event.pixel[1] - dragStartPixel[1];

    const layout = feature.get(FeatureProperty.LAYOUT);
    layout.setAttachmentPosition(attachmentIndex, originalAttachmentPixel[0] + dx, originalAttachmentPixel[1] + dy);
    feature.changed();
    this._dragInfo.dragged = true;

    return true;
  }

  _handleUpEvent () {
    let handled = false;
    if (this._dragInfo && this._dragInfo.dragged) {
      this._source.reprocessFeatures();
      handled = true;
    }
    this._dragInfo = undefined;
    return handled;
  }

}
